import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Input, SearchableSelectBox, Modal } from 'components';

import {
  updateUserInfoField,
  userInfoSelector,
  isUserCompanySelector,
} from 'redux/slices/signUp/signUpSlice';
import CurrentView from 'pages/Modules/Definitions/CurrentView';
import { useGetSearchTaxOfficeQuery } from 'redux/slices/address/addressApi';
import { useGetPhoneCodeListQuery } from 'redux/slices/phoneCode/phoneCodeApi';
import ActionButtons from '../ActionButtons/ActionButtons';

const CompanyInfoForm = ({ onPrev, usingFromRegister }) => {
  const dispatch = useDispatch();
  const userInfo = useSelector(userInfoSelector);
  const isUserCompany = useSelector(isUserCompanySelector);

  const [showModal, setShowModal] = useState(false);
  const [taxOfficeNumber, setTaxOfficeNumber] = useState('');
  const [companySearch, setCompanySearch] = useState({
    value: '',
    label: '',
  });
  const [telephoneCountryCode, setTelephoneCountryCode] = useState({
    value: '',
    label: '',
  });
  const [mobileCountryCode, setMobileCountryCode] = useState({
    value: '',
    label: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [searchTaxOffice, setSearchTaxOffice] = useState('');

  const { data: phoneCodes } = useGetPhoneCodeListQuery();

  const { data: taxOffices, isFetching: i2 } = useGetSearchTaxOfficeQuery(
    {
      text: searchTaxOffice,
    },
    { skip: searchTaxOffice.length < 3 }
  );
  const { formState, setValue } = useFormContext();
  const { errors } = formState;

  useEffect(() => setFormErrors({ ...errors }), [errors]);

  useEffect(() => {
    setValue('companyName', userInfo?.companyName);
    setValue('mobileAreaCode', userInfo?.mobileAreaCode);
    setValue('telephoneAreaCode', userInfo?.telephoneAreaCode);
    setCompanySearch({
      label: userInfo?.companyName,
      value: companySearch.value,
    });
    setValue('telephoneCountryCode', userInfo?.telephoneCountryCode);
    setTelephoneCountryCode({
      label: userInfo?.telephoneCountryCode,
      value: telephoneCountryCode.value,
    });
    setValue('telephone', userInfo?.telephone);
    setValue('mobileCountryCode', userInfo?.mobileCountryCode);
    setMobileCountryCode({
      label: userInfo?.mobileCountryCode,
      value: mobileCountryCode.value,
    });
    setValue('mobile', userInfo?.mobile);
    setValue('taxOffice', userInfo?.taxOffice);
    setValue('taxNumber', userInfo?.taxNumber);
    setSearchTaxOffice(userInfo?.taxOffice);
    setTaxOfficeNumber(userInfo?.taxOffice?.split('-')[1]);
  }, [userInfo]);

  const handleChangeUserInfo = (field, value) => {
    dispatch(updateUserInfoField({ field, value }));
  };

  const handleSelectTelephoneCode = (item) => {
    dispatch(
      updateUserInfoField({
        field: 'telephoneCountryCode',
        value: item.value.split('_')[1],
      })
    );
    setTelephoneCountryCode(item);
  };

  const handleSelectMobileCode = (item) => {
    dispatch(
      updateUserInfoField({
        field: 'mobileCountryCode',
        value: item.value.split('_')[1],
      })
    );
    setMobileCountryCode(item);
  };

  const handleOnSearchTaxOffice = (item) => {
    setTaxOfficeNumber(item.label.split('-')[1]);
    setSearchTaxOffice(item.label);
    dispatch(updateUserInfoField({ field: 'taxOffice', value: item.label }));
  };

  return (
    <>
      <div className="flex w-full overflow-y-auto sm:h-full sm:gap-x-4 relative">
        <div className="flex flex-col w-full gap-y-4">
          <Input
            required
            label="Firma Adı"
            name="companyName"
            id="companyName"
            value={userInfo.companyName}
            onChange={(e) =>
              handleChangeUserInfo(e.target.name, e.target.value)
            }
            errors={formErrors?.companyName?.message}
          />

          <div className="flex items-end">
            <SearchableSelectBox
              maxLength={20}
              className="z-20 w-1/4"
              selectListClassName="w-72"
              inputValue={telephoneCountryCode.label}
              onChange={(text) =>
                setTelephoneCountryCode({ label: text, value: '' })
              }
              onSelect={handleSelectTelephoneCode}
              label="Ülke Kodu"
              items={
                telephoneCountryCode?.label
                  ? phoneCodes?.data
                      ?.filter(
                        (phoneCode) =>
                          phoneCode.country
                            .toLocaleUpperCase()
                            .includes(
                              telephoneCountryCode.label.toLocaleUpperCase()
                            ) ||
                          phoneCode.phoneCode.includes(
                            telephoneCountryCode.label
                          )
                      )
                      ?.map((phoneCode) => {
                        return {
                          label: `${phoneCode.country} (${phoneCode.phoneCode})`,
                          value: phoneCode.country + '_' + phoneCode.phoneCode,
                        };
                      })
                  : phoneCodes?.data?.map((phoneCode) => {
                      return {
                        label: `${phoneCode.country} (${phoneCode.phoneCode})`,
                        value: phoneCode.country + '_' + phoneCode.phoneCode,
                      };
                    })
              }
              required
              inputErrors={formErrors?.telephoneCountryCode?.message}
            />
            <Input
              maxLength={20}
              className="w-1/4"
              required
              label="Alan Kodu"
              name="telephoneAreaCode"
              id="telephoneAreaCode"
              type="number"
              value={userInfo?.telephoneAreaCode}
              onChange={(e) => {
                handleChangeUserInfo(e.target.name, e.target.value);
              }}
              errors={formErrors?.telephoneAreaCode?.message && ' '}
            />
            <Input
              className="w-3/4"
              required
              label="İrtibat Numarası"
              name="telephone"
              id="telephone"
              maxLength={25}
              value={userInfo?.telephone}
              onChange={(e) => {
                !isNaN(e.target.value) &&
                  handleChangeUserInfo(e.target.name, e.target.value);
              }}
              errors={formErrors?.telephone?.message}
            />
          </div>

          <div className="flex items-end">
            <SearchableSelectBox
              className="z-10 w-1/4"
              selectListClassName="w-72"
              inputValue={mobileCountryCode.label}
              onChange={(text) =>
                setMobileCountryCode({ label: text, value: '' })
              }
              onSelect={handleSelectMobileCode}
              label="Ülke Kodu"
              items={
                mobileCountryCode?.label
                  ? phoneCodes?.data
                      ?.filter(
                        (phoneCode) =>
                          phoneCode.country
                            .toLocaleUpperCase()
                            .includes(
                              mobileCountryCode.label.toLocaleUpperCase()
                            ) ||
                          phoneCode.phoneCode.includes(mobileCountryCode.label)
                      )
                      ?.map((phoneCode) => {
                        return {
                          label: `${phoneCode.country} (${phoneCode.phoneCode})`,
                          value: phoneCode.country + '_' + phoneCode.phoneCode,
                        };
                      })
                  : phoneCodes?.data?.map((phoneCode) => {
                      return {
                        label: `${phoneCode.country} (${phoneCode.phoneCode})`,
                        value: phoneCode.country + '_' + phoneCode.phoneCode,
                      };
                    })
              }
              inputErrors={formErrors?.mobileCountryCode?.message}
            />
            <Input
              className="w-1/4"
              label="Alan Kodu"
              name="mobileAreaCode"
              id="mobileAreaCode"
              type="number"
              value={userInfo?.mobileAreaCode}
              onChange={(e) => {
                handleChangeUserInfo(e.target.name, e.target.value);
              }}
              errors={formErrors?.mobileAreaCode?.message}
            />
            <Input
              className="w-3/4"
              label="Cep Telefonu"
              name="mobile"
              id="mobile"
              maxLength={25}
              value={userInfo?.mobile}
              onChange={(e) => {
                !isNaN(e.target.value) &&
                  handleChangeUserInfo(e.target.name, e.target.value);
              }}
              errors={formErrors?.mobile?.message}
            />
          </div>

          {isUserCompany ? (
            <>
              <SearchableSelectBox
                maxLength={200}
                required
                inputValue={searchTaxOffice}
                placeholder="Vergi Dairesi"
                onChange={setSearchTaxOffice}
                onSelect={handleOnSearchTaxOffice}
                label="Vergi Dairesi"
                inputName="taxOffice"
                items={
                  taxOffices?.data.map((taxOffice) => {
                    return {
                      label: taxOffice.taxOffice1 + '-' + taxOffice.taxNo,
                      value: taxOffice.taxOffice1,
                    };
                  }) || []
                }
                isLoading={i2}
                inputErrors={formErrors?.taxOffice?.message}
              />
              <Input
                label="Vergi Daire Numarası"
                name="taxNumber"
                id="taxNumber"
                value={taxOfficeNumber}
                disabled={true}
              />
            </>
          ) : null}
        </div>
      </div>
      <div className="flex w-full justify-end mt-10">
        <ActionButtons onPrev={onPrev} />
      </div>
      {!usingFromRegister && (
        <Modal
          isOpen={showModal}
          handleModalClose={() => setShowModal(false)}
          ModalClass="bg-sky-light"
        >
          <div className="flex flex-col p-1 text-center gap-y-4">
            <CurrentView company={companySearch} />
          </div>
        </Modal>
      )}
    </>
  );
};

CompanyInfoForm.propTypes = {
  onPrev: PropTypes.func,
  usingFromRegister: PropTypes.bool,
};

export default CompanyInfoForm;
